import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Slide,
  TextField,
  IconButton,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  FormControl,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  LOAD_ADDRESS_BY_ZIP_CODE,
  getAddressByZipCode
} from 'src/store/actions/clientActions';
import DropdownMenu from 'src/components/DropdownMenu';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import clsx from 'clsx';
import { countriesList } from 'src/utils/constants/countries';
import makeInitialState from './initialState';
import useStyles from './styles';
import validationSchema from './schema';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AddressDialog = ({
  open,
  address,
  handleClose,
  submit,
  useExistingAddress = false,
  existingAddressOptions = [],
  includeCountry = false,
  editableCountry = false,
  includeMunicipality = true
}) => {
  const { classes } = useStyles();
  const initialState = useMemo(() => makeInitialState(address), [address]);
  const [useCopomex, setUseCopomex] = useState(
    includeCountry ? initialState.country === 'MX' : true
  );
  const resetRef = useRef();
  const dispatch = useDispatch();
  const [addressData, setAddressData] = useState({
    cities: [],
    colonias: [],
    states: [],
    isValid: null
  });
  const isLoadingAddress = useSelector(
    isLoadingSelector([LOAD_ADDRESS_BY_ZIP_CODE.action])
  );
  const { t } = useTranslation();
  const [checkboxState, setCheckboxState] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState({});

  const handleZipCodeChange = useCallback((e, setFieldValue) => {
    const { value } = e.target;
    setFieldValue('colonia', '');
    setFieldValue('city', '');
    setFieldValue('state', '');
    setFieldValue('municipality', '');
    if (value.length === 5 && useCopomex) {
      dispatch(getAddressByZipCode(value, setAddressData));
    }
  }, [useCopomex, dispatch]);

  const canSubmit = !useCopomex || addressData.isValid === true;

  const schema = useMemo(
    () => validationSchema(addressData, isLoadingAddress),
    [addressData, isLoadingAddress]
  );

  const handleOuterSubmit = useCallback(
    (values, { resetForm }) => {
      submit(values);
      resetForm(initialState);
      handleClose();
    },
    [submit, handleClose, initialState]
  );

  const handleCountryChange = useCallback(
    (value, setFieldValue) => {
      setFieldValue('country', value.key ?? null);
      setFieldValue('zipCode', '');
      setFieldValue('city', '');
      setFieldValue('colonia', '');
      setFieldValue('state', '');
      setFieldValue('municipality', '');
      if (value.key === 'MX') {
        setUseCopomex(true);
      } else {
        setUseCopomex(false);
      }
    },
    [setUseCopomex]
  );

  useEffect(() => {
    if (open) {
      setAddressData({
        cities: [],
        colonias: [],
        states: [],
        isValid: null
      });
      if (address && address.zipCode) {
        setUseCopomex(true);
        dispatch(getAddressByZipCode(address.zipCode, setAddressData));
      } else {
        const shouldUseCopomex =
          initialState.country === 'MX' || !initialState.country;
        setUseCopomex(shouldUseCopomex);
      }
    }
    if (!open) {
      setCheckboxState(false);
      setSelectedAddress({});
      setAddressData({
        cities: [],
        colonias: [],
        states: [],
        isValid: null
      });
    }
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, open, address, initialState.country, dispatch]);

  if (!open) {
    return null;
  }

  return (
    <Dialog
      disableEscapeKeyDown
      classes={{ paper: classes.root }}
      open={open}
      TransitionComponent={Transition}
      keepMounted
    >
      <DialogTitle className={classes.header} id="form-address-dialog-title">
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={() => {
            handleClose();
          }}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.header} color="primary" variant="h2">
          {t('CLIENTS_VIEW.CLIENT_DETAIL.CLIENT_ADDRESS')}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ marginBottom: '16px' }}>
        <Formik
          initialValues={initialState}
          validationSchema={schema}
          onSubmit={handleOuterSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            touched,
            values,
            resetForm
          }) => {
            const showCopomexError = !canSubmit && values.zipCode?.length === 5;
            const disableDependentFields =
              !values.zipCode || (includeCountry && !values.country);
            resetRef.current = () => resetForm(initialState);
            return (
              <form onSubmit={handleSubmit}>
                {useExistingAddress && existingAddressOptions.length && (
                  <FormControlLabel
                    key={checkboxState}
                    control={
                      <Checkbox
                        name="useExistingAddress"
                        color="primary"
                        checked={checkboxState}
                        onChange={() => {
                          setCheckboxState(!checkboxState);
                        }}
                      />
                    }
                    label={t('COMMON.USE_EXISTING_ADDRESS')}
                  />
                )}

                {checkboxState && (
                  <DropdownMenu
                    list={existingAddressOptions.map(item => ({
                      name: `Aval - ${item.fullName}`,
                      key: item
                    }))}
                    value={selectedAddress}
                    setValue={value => {
                      setSelectedAddress(value);
                      setFieldValue('street', value.street);
                      setFieldValue(
                        'externalHomeNumber',
                        value.externalHomeNumber
                      );
                      setFieldValue('insideHomeNumber', value.insideHomeNumber);
                      setFieldValue('zipCode', value.zipCode);
                      dispatch(
                        getAddressByZipCode(value.zipCode, setAddressData)
                      );
                      setFieldValue('city', value.city);
                      setFieldValue('colonia', value.colonia);
                      setFieldValue('state', value.state);
                      setFieldValue('municipality', value.municipality);
                    }}
                    label={t('COMMON.AVAL')}
                  />
                )}

                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={Boolean(touched.street && errors.street)}
                  fullWidth
                  helperText={touched.street && t(errors.street)}
                  label={t('COMMON.STREET')}
                  margin="normal"
                  name="street"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.street}
                  variant="outlined"
                />

                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={Boolean(
                    touched.externalHomeNumber && errors.externalHomeNumber
                  )}
                  fullWidth
                  helperText={
                    touched.externalHomeNumber && t(errors.externalHomeNumber)
                  }
                  label={t('COMMON.EXTERNAL_HOME_NUMBER')}
                  margin="normal"
                  name="externalHomeNumber"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.externalHomeNumber}
                  variant="outlined"
                />
                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={Boolean(
                    touched.insideHomeNumber && errors.insideHomeNumber
                  )}
                  fullWidth
                  helperText={
                    touched.insideHomeNumber && t(errors.insideHomeNumber)
                  }
                  label={t('COMMON.INSIDE_HOME_NUMBER')}
                  margin="normal"
                  name="insideHomeNumber"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.insideHomeNumber}
                  variant="outlined"
                />

                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={
                    Boolean(touched.zipCode && errors.zipCode) ||
                    showCopomexError
                  }
                  fullWidth
                  helperText={
                    showCopomexError
                      ? t('ERRORS.ZIP_CODE_WAS_NOT_FOUND')
                      : touched.zipCode && t(errors.zipCode)
                  }
                  label={t('COMMON.ZIP_CODE')}
                  margin="normal"
                  name="zipCode"
                  onBlur={handleBlur}
                  disabled={includeCountry && !values.country}
                  onChange={e => {
                    if (useCopomex) {
                      handleZipCodeChange(e, setFieldValue);
                    }
                    handleChange(e);
                  }}
                  type="text"
                  value={values.zipCode}
                  variant="outlined"
                />

                {useCopomex ? (
                  <>
                    <FormControl
                      fullWidth
                      error={Boolean(touched.city && errors.city)}
                    >
                      <Autocomplete
                        key={`city-${values.zipCode}`}
                        options={
                          addressData?.cities.map(city => ({
                            key: city,
                            name: city
                          })) ?? []
                        }
                        getOptionLabel={option => option?.name ?? ''}
                        onChange={(e, value) => {
                          setFieldValue('city', value ? value.name : '');
                        }}
                        value={
                          values.city
                            ? (addressData?.cities ?? [])
                                .map(city => ({ key: city, name: city }))
                                .find(city => city.name === values.city) ?? {
                                key: values.city,
                                name: values.city
                              }
                            : null
                        }
                        loading={isLoadingAddress}
                        clearOnBlur
                        selectOnFocus={false}
                        handleHomeEndKeys={false}
                        freeSolo
                        inputValue={values.city ?? ''}
                        onInputChange={(_, value) => {
                          setFieldValue('city', value);
                        }}
                        sx={{ marginBottom: 2, marginTop: 2 }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t('COMMON.CITY')}
                            variant="outlined"
                            fullWidth
                            disabled={disableDependentFields}
                            helperText={touched.city && t(errors.city)}
                            error={Boolean(touched.city && errors.city)}
                          />
                        )}
                        loadingText={t('COMMON.LOADING')}
                      />
                    </FormControl>

                    <FormControl
                      fullWidth
                      error={Boolean(touched.colonia && errors.colonia)}
                    >
                      <Autocomplete
                        options={
                          addressData?.colonias?.map(col => ({
                            key: col,
                            name: col
                          })) ?? []
                        }
                        getOptionLabel={option => option?.name ?? ''}
                        onChange={(e, value) => {
                          setFieldValue('colonia', value ? value.name : '');
                        }}
                        value={
                          (addressData?.colonias ?? [])
                            .map(col => ({ key: col, name: col }))
                            .find(col => col.name === values.colonia) ?? {
                            key: values.colonia ?? '',
                            name: values.colonia ?? ''
                          }
                        }
                        loading={isLoadingAddress}
                        clearOnBlur
                        selectOnFocus={false}
                        handleHomeEndKeys={false}
                        freeSolo
                        inputValue={values.colonia ?? ''}
                        onInputChange={(_, value) => {
                          setFieldValue('colonia', value);
                        }}
                        sx={{ marginBottom: 2, marginTop: 2 }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t('COMMON.COLONIA')}
                            variant="outlined"
                            fullWidth
                            disabled={disableDependentFields}
                            error={Boolean(touched.colonia && errors.colonia)}
                            helperText={touched.colonia && t(errors.colonia)}
                          />
                        )}
                        loadingText={t('COMMON.LOADING')}
                      />
                    </FormControl>

                    {includeMunicipality && (
                      <FormControl
                        fullWidth
                        error={Boolean(
                          touched.municipality && errors.municipality
                        )}
                      >
                        <Autocomplete
                          options={
                            addressData?.municipalities?.map(municipality => ({
                              key: municipality,
                              name: municipality
                            })) ?? []
                          }
                          getOptionLabel={option => option?.name ?? ''}
                          onChange={(e, value) => {
                            setFieldValue(
                              'municipality',
                              value ? value.name : ''
                            );
                          }}
                          value={
                            (addressData?.municipalities ?? [])
                              .map(municipality => ({
                                key: municipality,
                                name: municipality
                              }))
                              .find(
                                municipality =>
                                  municipality.name === values.municipality
                              ) ?? {
                              key: values.municipality ?? '',
                              name: values.municipality ?? ''
                            }
                          }
                          loading={isLoadingAddress}
                          clearOnBlur
                          selectOnFocus={false}
                          handleHomeEndKeys={false}
                          freeSolo
                          inputValue={values.municipality ?? ''}
                          onInputChange={(_, value) => {
                            setFieldValue('municipality', value);
                          }}
                          sx={{ marginBottom: 2, marginTop: 2 }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label={t('COMMON.MUNICIPALITY')}
                              variant="outlined"
                              fullWidth
                              disabled={disableDependentFields}
                              error={Boolean(
                                touched.municipality && errors.municipality
                              )}
                              helperText={
                                touched.municipality && t(errors.municipality)
                              }
                            />
                          )}
                          loadingText={t('COMMON.LOADING')}
                        />
                      </FormControl>
                    )}

                    <FormControl
                      fullWidth
                      error={Boolean(touched.state && errors.state)}
                    >
                      <Autocomplete
                        options={
                          addressData?.states?.map(state => ({
                            key: state,
                            name: state
                          })) ?? []
                        }
                        getOptionLabel={option => option?.name ?? ''}
                        onChange={(e, value) => {
                          setFieldValue('state', value ? value.name : '');
                        }}
                        value={
                          (addressData?.states ?? [])
                            .map(state => ({ key: state, name: state }))
                            .find(state => state.name === values.state) ?? {
                            key: values.state ?? '',
                            name: values.state ?? ''
                          }
                        }
                        loading={isLoadingAddress}
                        clearOnBlur
                        selectOnFocus={false}
                        handleHomeEndKeys={false}
                        freeSolo
                        inputValue={values.state ?? ''}
                        onInputChange={(_, value) => {
                          setFieldValue('state', value);
                        }}
                        sx={{ marginBottom: 2, marginTop: 2 }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            variant="outlined"
                            fullWidth
                            disabled={disableDependentFields}
                            label={t('COMMON.STATE')}
                            error={Boolean(touched.state && errors.state)}
                            helperText={touched.state && t(errors.state)}
                          />
                        )}
                        loadingText={t('COMMON.LOADING')}
                      />
                    </FormControl>
                  </>
                ) : (
                  <>
                    <TextField
                      InputProps={{
                        autoComplete: 'off'
                      }}
                      error={Boolean(touched.city && errors.city)}
                      fullWidth
                      helperText={touched.city && t(errors.city)}
                      label={t('COMMON.CITY')}
                      margin="normal"
                      name="city"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="text"
                      value={values.city}
                      variant="outlined"
                      disabled={disableDependentFields}
                    />

                    <TextField
                      InputProps={{
                        autoComplete: 'off'
                      }}
                      error={Boolean(touched.colonia && errors.colonia)}
                      fullWidth
                      helperText={touched.colonia && t(errors.colonia)}
                      label={t('COMMON.COLONIA')}
                      margin="normal"
                      name="colonia"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="text"
                      value={values.colonia}
                      variant="outlined"
                      disabled={disableDependentFields}
                    />

                    {includeMunicipality && (
                      <TextField
                        InputProps={{
                          autoComplete: 'off'
                        }}
                        error={Boolean(
                          touched.municipality && errors.municipality
                        )}
                        fullWidth
                        helperText={
                          touched.municipality && t(errors.municipality)
                        }
                        label={t('COMMON.MUNICIPALITY')}
                        margin="normal"
                        name="municipality"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="text"
                        value={values.municipality}
                        variant="outlined"
                        disabled={disableDependentFields}
                      />
                    )}

                    <TextField
                      InputProps={{
                        autoComplete: 'off'
                      }}
                      error={Boolean(touched.state && errors.state)}
                      fullWidth
                      helperText={touched.state && t(errors.state)}
                      label={t('COMMON.STATE')}
                      margin="normal"
                      name="state"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="text"
                      value={values.state}
                      variant="outlined"
                      disabled={disableDependentFields}
                    />
                  </>
                )}

                {includeCountry && (
                  <FormControl fullWidth>
                    <Autocomplete
                      disabled={!editableCountry}
                      options={countriesList}
                      getOptionLabel={option =>
                        option?.name ? t(option.name) : ''
                      }
                      onChange={(_e, value) => {
                        handleCountryChange(value, setFieldValue);
                      }}
                      value={countriesList.find(
                        country => country?.key === values.country
                      )}
                      loading={isLoadingAddress}
                      clearOnBlur
                      selectOnFocus={false}
                      handleHomeEndKeys={false}
                      freeSolo={false}
                      sx={{ marginBottom: 2, marginTop: 2 }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={t('COMMON.COUNTRY')}
                          variant="outlined"
                          fullWidth
                          helperText={touched.country && t(errors.country)}
                          error={Boolean(touched.country && errors.country)}
                        />
                      )}
                      loadingText={t('COMMON.LOADING')}
                    />
                  </FormControl>
                )}

                <Grid container direction="column" alignItems="stretch">
                  <Grid item className={classes.buttons}>
                    <Button
                      className={clsx(
                        classes.label,
                        classes.buttons,
                        canSubmit ? classes.buttonMain : classes.buttonDisabled
                      )}
                      type="submit"
                      color="primary"
                      variant="outlined"
                      disabled={!canSubmit}
                    >
                      {t('COMMON.SAVE')}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

AddressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  address: PropTypes.object,
  useExistingAddress: PropTypes.bool.isRequired,
  existingAddressOptions: PropTypes.array,
  includeCountry: PropTypes.bool,
  editableCountry: PropTypes.bool,
  includeMunicipality: PropTypes.bool
};

export default AddressDialog;
